import type { ApiResponse, PagedResponseDto } from "@/types/common";
import type {
  OrderCreateDto,
  OrderFilterParams,
  OrderListDto,
  OrderResponseDto,
  OrderStatusUpdateDto,
  StorePosMemberLookupDto,
  StorePosOrderCreateDto,
} from "@/types/order.dto";
import { apiClient, unwrapApiResponse } from "./httpClient";

export const ordersService = {
  create(dto: OrderCreateDto): Promise<OrderResponseDto> {
    return unwrapApiResponse(apiClient.post<ApiResponse<OrderResponseDto>>("/Orders", dto));
  },

  lookupPosMember(storeId: number, memberCode: string): Promise<StorePosMemberLookupDto> {
    return unwrapApiResponse(
      apiClient.get<ApiResponse<StorePosMemberLookupDto>>(`/Orders/store/${storeId}/pos/member`, {
        params: { memberCode },
      }),
    );
  },

  createStorePosOrder(storeId: number, dto: StorePosOrderCreateDto): Promise<OrderResponseDto> {
    return unwrapApiResponse(
      apiClient.post<ApiResponse<OrderResponseDto>>(`/Orders/store/${storeId}/pos`, dto),
    );
  },

  getById(id: number): Promise<OrderResponseDto> {
    return unwrapApiResponse(apiClient.get<ApiResponse<OrderResponseDto>>(`/Orders/${id}`));
  },

  getByNumber(orderNumber: string): Promise<OrderResponseDto> {
    return unwrapApiResponse(
      apiClient.get<ApiResponse<OrderResponseDto>>(`/Orders/number/${orderNumber}`),
    );
  },

  getMyOrders(filter: OrderFilterParams = {}): Promise<PagedResponseDto<OrderListDto>> {
    return unwrapApiResponse(
      apiClient.get<ApiResponse<PagedResponseDto<OrderListDto>>>("/Orders/my-orders", { params: filter }),
    );
  },

  getAll(filter: OrderFilterParams = {}): Promise<PagedResponseDto<OrderListDto>> {
    return unwrapApiResponse(
      apiClient.get<ApiResponse<PagedResponseDto<OrderListDto>>>("/Orders", { params: filter }),
    );
  },

  updateStatus(id: number, dto: OrderStatusUpdateDto): Promise<OrderResponseDto> {
    return unwrapApiResponse(
      apiClient.put<ApiResponse<OrderResponseDto>>(`/Orders/${id}/status`, dto),
    );
  },

  cancel(id: number): Promise<boolean> {
    return unwrapApiResponse(apiClient.post<ApiResponse<boolean>>(`/Orders/${id}/cancel`));
  },

  getStoreOrders(storeId: number, filter: OrderFilterParams = {}): Promise<PagedResponseDto<OrderListDto>> {
    return unwrapApiResponse(
      apiClient.get<ApiResponse<PagedResponseDto<OrderListDto>>>(`/Orders/store/${storeId}`, {
        params: filter,
      }),
    );
  },

  confirmOrder(id: number, storeId: number): Promise<OrderResponseDto> {
    return unwrapApiResponse(
      apiClient.post<ApiResponse<OrderResponseDto>>(`/Orders/${id}/store/${storeId}/confirm`),
    );
  },

  markReady(id: number, storeId: number): Promise<OrderResponseDto> {
    return unwrapApiResponse(
      apiClient.post<ApiResponse<OrderResponseDto>>(`/Orders/${id}/store/${storeId}/ready`),
    );
  },

  getDeliveryPartnerOrders(
    deliveryPartnerId: number,
    filter: OrderFilterParams = {},
  ): Promise<PagedResponseDto<OrderListDto>> {
    return unwrapApiResponse(
      apiClient.get<ApiResponse<PagedResponseDto<OrderListDto>>>(
        `/Orders/delivery-partner/${deliveryPartnerId}`,
        { params: filter },
      ),
    );
  },

  markPickedUp(id: number, deliveryPartnerId: number): Promise<boolean> {
    return unwrapApiResponse(
      apiClient.post<ApiResponse<boolean>>(
        `/Orders/${id}/delivery-partner/${deliveryPartnerId}/picked-up`,
      ),
    );
  },

  markDelivered(id: number, deliveryPartnerId: number, otp: string): Promise<boolean> {
    return unwrapApiResponse(
      apiClient.post<ApiResponse<boolean>>(
        `/Orders/${id}/delivery-partner/${deliveryPartnerId}/delivered`,
        null,
        { params: { otp } },
      ),
    );
  },
};
